import { useNavigate } from "react-router-dom";
import { endPoints } from "../constants/urlConstants";
import { deleteCall, getCall, postCall } from "../services/apiService";
import {
  getItemInLocalStorage,
  removeItemInLocalStorage,
  setItemInLocalStorage,
} from "../services/localStorage";
import CryptoJS from "crypto-js";
import { detectBrowser } from "./detectBrowser";

export function formatDateTime(
  dateTimeString: string,
  options: {
    showDayOfWeek?: boolean;
    use24HourFormat?: boolean;
  } = {},
): string {
  // Parse the input string manually to ensure correct IST interpretation
  const [datePart, timePart] = dateTimeString.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);

  // Create a date object in local time zone (which should be IST)
  const date = new Date(year, month - 1, day, hour, minute);

  // Get day of the week
  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const dayOfWeek = daysOfWeek[date.getDay()];

  // Get hours and minutes
  let hours = date.getHours();
  const minutes = date.getMinutes();

  let timeString = "";

  if (options.use24HourFormat) {
    // 24-hour format
    timeString = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  } else {
    // 12-hour format
    const ampm = hours >= 12 ? "p.m." : "a.m.";
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    timeString = `${hours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  }

  // Construct the formatted string
  return options.showDayOfWeek ? `${dayOfWeek}, ${timeString}` : timeString;
}

export function formatDateTimeCustom(dateTimeString: string): string {
  // Parse the input string manually to ensure correct IST interpretation
  const [datePart, timePart] = dateTimeString.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);

  // Create a date object in local time zone (which should be IST)
  const date = new Date(year, month - 1, day, hour, minute);

  // Get month abbreviation
  const months = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];
  const monthAbbr = months[date.getMonth()];

  // Get day of the week
  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const dayOfWeek = daysOfWeek[date.getDay()];

  // Get hours and minutes
  let hours = date.getHours();
  const minutes = date.getMinutes();

  // 12-hour format
  const ampm = hours >= 12 ? "p.m." : "a.m.";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  // Construct the formatted string
  return `${monthAbbr} ${day.toString().padStart(2, "0")}, ${dayOfWeek} at ${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${ampm}`;
}

export function getMonthAbbreviation(dateTimeString: string): string {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Extract the month from the date string
  if (!dateTimeString) {
    return "";
  }
  const monthPart = dateTimeString.split("-")[1];
  const monthIndex = parseInt(monthPart, 10) - 1;
  return months[monthIndex] || "";
}

export function getDateFromString(dateTimeString: string): number {
  if (!dateTimeString) return " -"; // Extract the date part from the string
  const datePart = dateTimeString?.split("T")[0];

  // Split the date part and get the day
  const day = parseInt(datePart?.split("-")[2], 10);

  return day.toString().padStart(2, "0");
}

export function formatTime(dateTimeString: string): string {
  // Extract the time part from the string
  const timePart = dateTimeString.split("T")[1];

  // Split the time part and get hours and minutes
  const [hours, minutes] = timePart.split(":").map(Number);

  // Determine if it's AM or PM
  const period = hours >= 12 ? "p.m." : "a.m.";

  // Convert to 12-hour format
  const hours12 = hours % 12 || 12;

  // Format the time string
  return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
}

export function subtractTime(
  dateTimeString: string,
  joinTime?: number,
): string {
  const [datePart, timePart] = dateTimeString.split("T");
  const [hours, minutes] = timePart.split(":").map(Number);

  // Create a local Date object (year, month-1, day, hours, minutes)
  const [year, month, day] = datePart.split("-").map(Number);
  const date = new Date(year, month - 1, day, hours, minutes);

  // Subtract joinTime if provided
  if (joinTime) {
    date.setMinutes(date.getMinutes() - joinTime);
  }

  const finalHours = date.getHours();
  const finalMinutes = date.getMinutes();
  const period = finalHours >= 12 ? "p.m." : "a.m.";
  const hours12 = finalHours % 12 || 12;

  return `${hours12}:${finalMinutes.toString().padStart(2, "0")} ${period}`;
}

export function getFutureTime(
  startDate: string,
  durationMinutes: number,
): string {
  // Parse the start date
  const [datePart, timePart] = startDate.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);

  // Create a Date object and add the duration
  const futureDate = new Date(year, month - 1, day, hour, minute);
  futureDate?.setMinutes(futureDate.getMinutes() + durationMinutes);

  // Extract hours and minutes from the future date
  let futureHours = futureDate.getHours();
  const futureMinutes = futureDate.getMinutes();

  // Determine AM or PM
  const period = futureHours >= 12 ? "p.m." : "a.m.";

  // Convert to 12-hour format
  futureHours = futureHours % 12 || 12;

  // Format the time string
  return `${futureHours}:${futureMinutes.toString().padStart(2, "0")} ${period}`;
}

/**
 * @description this function is used to parse the ISO date to local date, when the dateString directly converting to date object it will converting to +5.30 hrs and making my day to next day
 * @param dateTimeString
 * @returns
 */
export function parseISOToLocalDate(dateTimeString: string): Date {
  // Parse the ISO string manually to avoid automatic time zone conversion
  const [datePart, timePart] = dateTimeString.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hours, minutes, seconds] = timePart.split(":").map((part) => {
    // Handle milliseconds and timezone information
    const [mainPart] = part.split(".");
    return Number(mainPart);
  });

  // Create a new Date object in local time
  const localDate = new Date(year, month - 1, day, hours, minutes, seconds);

  return localDate;
}

export function getDayOfWeek(dateTimeString: string): string {
  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  // Create a Date object from the input string
  const date = dateTimeString && parseISOToLocalDate(dateTimeString);

  // Get the day of the week (0-6, where 0 is Sunday)
  const dayIndex = date ? date.getDay() : 0;

  // Return the corresponding day name
  return daysOfWeek[dayIndex];
}

// Utility function to capitalize each word
export const capitalizeWords = (str: string) =>
  str?.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));

export function formatDateTimeWithHiphens(
  dateTimeString: string,
  includeTime: boolean = true,
): string {
  // Parse the input string manually to ensure correct IST interpretation
  const [datePart, timePart] = dateTimeString.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);

  // Create a date object in local time zone (which should be IST)
  const date = new Date(year, month - 1, day, hour, minute);

  // Get month abbreviation
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthAbbr = months[date.getMonth()];

  // Get day of the week
  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const dayOfWeek = daysOfWeek[date.getDay()];

  // Get hours and minutes
  let hours = date.getHours();
  const minutes = date.getMinutes();

  // 12-hour format
  const ampm = hours >= 12 ? "p.m" : "a.m.";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  // Base formatted string
  const dateString = `${dayOfWeek}, ${day.toString().padStart(2, "0")}-${monthAbbr}-${year.toString()}`;

  // Add time only if includeTime is true
  if (includeTime) {
    return `${dateString} at ${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  }

  return dateString;
}

export function calculateTimeForMeeting(
  meetingDateTimeString: string,
): string | null {
  // console.log(meetingDateTimeString, "meetingDateTimeString");
  const currentDateTime = new Date();
  // const meetingDateTime = new Date(meetingDateTimeString);

  // Parse the ISO string manually to avoid automatic time zone conversion
  const [datePart, timePart] = meetingDateTimeString.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hoursEnd, minutesEnd, secondsEnd] = timePart.split(":").map((part) => {
    // Handle milliseconds and timezone information
    const [mainPart] = part.split(".");
    return Number(mainPart);
  });

  // Create a new Date object in local time
  const meetingDateTime = new Date(
    year,
    month - 1,
    day,
    hoursEnd,
    minutesEnd,
    secondsEnd,
  );

  // Calculate the time difference in milliseconds
  const timeDifference =
    meetingDateTime?.getTime() - currentDateTime?.getTime();

  if (timeDifference <= 0) {
    // If the meeting time is now or has already passed
    return null;
  }

  const seconds = Math.floor(timeDifference / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  // Pluralization helper
  const pluralize = (value: number, unit: string) =>
    `${value} ${unit}${value === 1 ? "" : "s"}`;

  if (days > 0) {
    const remainingHours = hours % 24;
    return `${pluralize(days, "day")} ${pluralize(remainingHours, "hr")}`;
  } else if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return `${pluralize(hours, "hr")} ${pluralize(remainingMinutes, "min")}`;
  } else if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return `${pluralize(minutes, "min")} ${pluralize(remainingSeconds, "sec")}`;
  } else {
    return `${pluralize(seconds, "sec")}`;
  }
}

// GET YEAR FROM DATE STRING
export function getYearBasedOnDate(dateTimeString: string): string {
  // Extract the year from the date string
  if (!dateTimeString) {
    return "";
  }
  return dateTimeString.split("-")[0] || "";
}

/**
 * Removes the country code from a phone number.
 * @param phoneNumber - The phone number with country code.
 * @param countryCode - The country code to remove.
 * @returns The phone number without the country code.
 */
export function removeCountryCode(
  phoneNumber: string,
  countryCode: string,
): string {
  // Ensure the country code starts with a plus sign
  if (countryCode.startsWith("+")) {
    countryCode = countryCode.slice(1);
  }

  // Remove the country code from the phone number
  if (phoneNumber.startsWith(countryCode)) {
    return phoneNumber.slice(countryCode.length);
  }
  console.log(phoneNumber.slice(countryCode.length), "phoneeea");

  // If the phone number does not start with the country code, return it as is
  return phoneNumber;
}

// Adjust the import path as necessary
export const fetchSearchMemberDetails = (
  userId: string,
  searchText: string,
  setLoading: (loading: boolean) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setSeekersData: (data: any) => void,
  setErrorMsg: (message: string) => void,
) => {
  setLoading(true);
  getCall(`${endPoints.getUsers}?user_id=${userId}&search_string=${searchText}`)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .then((response: any) => {
      if (response?.data?.statusCode === 200) {
        setLoading(false);
        setSeekersData(response?.data?.data);
      } else {
        setLoading(false);
        console.error("Error:", response?.data?.message);
        setErrorMsg(response?.data?.message);
      }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .catch((error: any) => {
      setLoading(false);
      console.error("Error:", error);
      setErrorMsg("Failed to fetch data");
    });
};

export const handleJoin = (
  verifiedSeekersIds: string[],
  isChecked: string[],
  setItemInLocalStorage: (key: string, value: unknown) => void,
  navigate: ReturnType<typeof useNavigate>,
) => {
  const attendanceIds = Array.from(
    new Set(
      [...verifiedSeekersIds, ...isChecked]?.filter(
        (id) => verifiedSeekersIds?.includes(id) && isChecked?.includes(id),
      ),
    ),
  );
  setItemInLocalStorage("verifiedSeekersIds", attendanceIds);
  navigate("/myspace/infinipath/zoom");
};

export const seekerExistanceCheck = (formattedPhone: unknown) => {
  const getUserPayload = {
    phone_number: formattedPhone,
    app_type: "infinipath",
  };
  return postCall(endPoints.getUser, getUserPayload)
    .then((response) => {
      if (response?.data?.statusCode === 200) {
        return response;
      }
      if (response?.data?.statusCode === 200) {
        return response;
      }
    })
    .catch((error) => {
      throw error;
    });
};

export const deleteSeekerAccount = (id: unknown) => {
  const seekerPayload = {
    // phone_number: phoneNumber,
    appType: "infinipath",
  };

  return deleteCall(`${endPoints.users}/${id}`, seekerPayload)
    .then((response) => {
      if (response?.data?.statusCode === 200) {
        return response;
      }
      return response;
    })
    .catch((error) => {
      throw error;
    });
};

export function formatDuration(minutes: number): string {
  // console.log(minutes, "minutes");
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const formattedHours = hours.toString().padStart(2, "0");

  const formattedMinutes = remainingMinutes.toString().padStart(2, "0");
  if (hours === 0) {
    return `${formattedMinutes}m`;
  } else if (remainingMinutes === 0) {
    return `${formattedHours}h`;
  } else {
    return `${formattedHours}h ${formattedMinutes}m`;
  }
}

export const convertToISOFormat = (
  dateString: string,
  timeString?: string | Date,
): string => {
  const date = new Date(dateString);

  if (timeString instanceof Date) {
    const hours = timeString?.getHours();
    const minutes = timeString?.getMinutes();
    date.setHours(hours, minutes, 0, 0);
  } else if (typeof timeString === "string") {
    const [hours, minutes] = timeString.split(":").map(Number);
    date.setHours(hours, minutes, 0, 0);
  }
  // Adjust for the local time zone offset
  const offset = date.getTimezoneOffset();
  date.setMinutes(date.getMinutes() - offset);

  return date.toISOString();
};

export const calculateEndTime = (
  startTime: string,
  duration: string,
): string => {
  const [time, period] = startTime.split(" ");
  const [hours, minutes] = time.split(":").map(Number);

  let durationMinutes = 0;
  if (duration.includes("h")) {
    const [hours, mins] = duration.split("h");
    durationMinutes = Number(hours) * 60;
    if (mins) {
      durationMinutes += Number(mins.replace("m", ""));
    }
  } else {
    durationMinutes = Number(duration.replace("m", ""));
  }

  let totalMinutes = hours * 60 + minutes;
  if (period === "PM" && hours !== 12) totalMinutes += 12 * 60;
  if (period === "AM" && hours === 12) totalMinutes = minutes;

  totalMinutes += durationMinutes;

  let newHours = Math.floor(totalMinutes / 60);
  const newMinutes = totalMinutes % 60;
  let newPeriod = "AM";

  if (newHours >= 12) {
    newPeriod = "PM";
    if (newHours > 12) newHours -= 12;
  }
  if (newHours === 0) newHours = 12;

  return `${newHours}:${newMinutes.toString().padStart(2, "0")} ${newPeriod}`;
};

export const convertTimeToMinutes = (timeString: string): string => {
  let totalMinutes = 0;

  // Extract hours and minutes using regular expressions
  const hoursMatch = timeString.match(/(\d+)h/);
  const minutesMatch = timeString.match(/(\d+)m/);

  // Convert hours to minutes and add to total minutes
  if (hoursMatch) {
    totalMinutes += parseInt(hoursMatch[1]) * 60;
  }

  // Add minutes to total minutes
  if (minutesMatch) {
    totalMinutes += parseInt(minutesMatch[1]);
  }

  return totalMinutes.toString();
};

export const formattedTime = (time: string): string => {
  return time.replace(/AM/, "a.m").replace(/PM/, "p.m").replace(/a.m./, "a.m");
};

export const replaceDashWithTo = (timeRange: string): string => {
  return timeRange.replace(" - ", " to ");
};

export const formatTimeString = (timeString: string): string => {
  const timePattern = /(\d{1,2})h\s*(\d{1,2})m/;
  const minutePattern = /(\d{1,2})m/;
  const hourPattern = /(\d{1,2})h/;
  if (timePattern.test(timeString)) {
    const match = timeString.match(timePattern);
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const hourLabel = hours === 1 ? "hour" : "hours";
    const minuteLabel = "min";
    return `${hours} ${hourLabel} ${minutes} ${minuteLabel}`;
  } else if (minutePattern.test(timeString)) {
    const match = timeString.match(minutePattern);
    const minutes = parseInt(match[1], 10);
    const minuteLabel = "min";
    return `${minutes} ${minuteLabel}`;
  } else if (hourPattern.test(timeString)) {
    const match = timeString.match(hourPattern);
    const hours = parseInt(match[1], 10);
    const hourLabel = hours === 1 ? "hour" : "hours";

    return `${hours} ${hourLabel}`;
  } else {
    throw new Error("Invalid time format");
  }
};

/**
 * @description this function is used to get the admin latest meeting data
 * @param setLoading
 * @param setAdminMeetingData
 */
export const FetchMeetingDetails = (
  setLoading: (loading: boolean) => void,
  setAdminMeetingData: (data: unknown) => void,
) => {
  const userData = getItemInLocalStorage("seekerDetails");
  // ${endPoints.webinars}/?user_id=${userData?.phoneNumber}
  setLoading(true);
  getCall(`${endPoints.admin}/${userData?.id}/webinars`)
    .then((res) => {
      if (res?.data?.statusCode === 200) {
        setLoading(false);
        setAdminMeetingData(res?.data.data);
        setItemInLocalStorage("table_meeting_id", res?.data?.data?.meetingId);
      } else {
        setLoading(false);
        // setErrorMsg(res?.data?.message);
      }
    })
    .catch((err) => {
      console.log(err);
      setLoading(false);
      // setErrorMsg("Failed to fetch data");
    })
    .finally(() => setLoading(false));
};
// Utility function to capitalize the first letter of a string
export const capitalizeFirstLetter = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

// Utility function to calculate the age from a date of birth string
export const calculateAge = (dobString: string): string => {
  if (!dobString) return "-"; // Handle empty values
  const dob = new Date(dobString);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDifference = today.getMonth() - dob.getMonth();
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < dob.getDate())
  ) {
    age--;
  }
  return age.toString();
};
export const FetchWebinarList = (
  setLoading: (loading: boolean) => void,
  setAdminMeetingData: (data: unknown) => void,
) => {
  const userData = getItemInLocalStorage("seekerDetails");
  setLoading(true);
  getCall(`${endPoints.webinars}?userId=${userData?.id}&page=1&limit=20`)
    .then((res) => {
      if (res?.data?.statusCode === 200) {
        setLoading(false);
        setAdminMeetingData(res?.data.data.data);
      } else {
        setLoading(false);
        // setErrorMsg(res?.data?.message);
      }
    })
    .catch((err) => {
      console.log(err);
      setLoading(false);
      // setErrorMsg("Failed to fetch data");
    })
    .finally(() => setLoading(false));
};

/**
 * @description Converting the age distribution to a combined format as per UI
 * @param agePercentages
 * @returns
 */
export const convertAgeDestribution = (agePercentages: unknown) => {
  const combinedAgePercentages = {
    "3-18": agePercentages["3-18"],
    "19-60":
      agePercentages["18-29"] +
      agePercentages["29-40"] +
      agePercentages["40-60"],
    "60+": agePercentages["60+"],
  };
  return combinedAgePercentages;
};

/**
 * @description Converting the method of attendance as per UI
 * @param agePercentages
 * @returns
 */
export const modifyMethodOfAttendance = (data: unknown) => {
  const modifiedData = {
    "Own device": data?.selfJoiningPercentage,
    "Other device": data?.joiningOnOtherDevicePercentage,
    "Cross device": data?.joiningWithOthersPercentage,
  };
  return modifiedData;
};

/**
 * @description Custom Numbers for method of attendance
 * @param agePercentages
 * @returns
 */
export const customNumbersMethodOfAttendance = (data: unknown) => {
  const modifiedData = {
    "Own device": data?.joiningModeData?.selfJoining,
    "Other device": data?.joiningModeData?.joiningOnOtherDevice,
    "Cross device": data?.joiningModeData?.joiningWithOthers,
  };
  return modifiedData;
};
interface DeviceData {
  deviceType: string;
  percentage: number;
}

export const convertArrayToObject = (
  data: DeviceData[],
): { [key: string]: number } => {
  return data?.reduce(
    (acc, item) => {
      acc[item?.deviceType] = item.percentage;
      return acc;
    },
    {} as { [key: string]: number },
  );
};

export const modifyAudienceDiscipline = (data: unknown) => {
  const modifiedData = {
    Cancellations: data?.cancellations || 0,
    "Video downgrades": data?.videoDowngraded || 0,
    "Late joiners count": data?.lateJoinersCount || 0,
    Rejoins: data?.rejoinersCount || 0,
    // "Drop off": data?.dropOff || 0,
    // Rejoins: data?.rejoins || 0,
  };
  return modifiedData;
};

/**
 * @description this function is used to mask the profile url
 * @param url
 * @returns url string
 */
export const modifyProfileUrl = (url: string): string => {
  const urlObject = new URL(url);
  // console.log(urlObject, "urlObject111");
  const newDomain = new URL(process.env.REACT_APP_WEB_URL);
  // console.log(newDomain, "newDomain");

  // Set the new protocol, hostname, and port
  urlObject.protocol = newDomain.protocol;
  urlObject.hostname = newDomain.hostname;
  // urlObject.port = newDomain.port;

  // console.log(urlObject, "urlObject222");
  console.log(urlObject.toString(), "browserbrowserbrowser");
  return urlObject.toString();
};

/**
 * @description this function is used to calculate the join button enable time
 * @param startDate meeting start date time
 * @param joinEnableMinutes
 * @returns join time string
 */
export const calculateJoinTime = (
  startDate: string,
  joinEnableMinutes: number,
): string => {
  const date = new Date(startDate);

  // Subtract the joinEnableMinutes from the startDate
  date?.setMinutes(date?.getMinutes() - joinEnableMinutes);

  // Format the date to the desired string format
  const year = date?.getUTCFullYear();
  const month = String(date?.getUTCMonth() + 1)?.padStart(2, "0");
  const day = String(date?.getUTCDate())?.padStart(2, "0");
  const hours = String(date?.getUTCHours())?.padStart(2, "0");
  const minutes = String(date?.getUTCMinutes())?.padStart(2, "0");
  const seconds = String(date?.getUTCSeconds())?.padStart(2, "0");
  const milliseconds = String(date?.getUTCMilliseconds())?.padStart(3, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
};
/**
 * @description this function is used to format the date to local ISO string
 * @param date
 * @returns date string
 **/
export const formatDateToLocalISOString = (
  date: Date | null,
): string | null => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const milliseconds = String(date.getMilliseconds()).padStart(3, "0");
  // Get the time zone offset in ±hh:mm format
  const offset = -date.getTimezoneOffset();
  const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(
    2,
    "0",
  );
  const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, "0");
  const offsetSign = offset >= 0 ? "+" : "-";
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
};

export const formatDateToYYYYMMDD = (date: Date | null): string | null => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getTagLineMessages = (members: unknown, flag: boolean): string => {
  const pural = members?.length > 1 ? "member(s)" : "member(s)";
  const myGroup = members?.length > 1 ? "seeker(s)" : "seeker(s)";
  if (flag) {
    const acceptedData = members?.filter(
      (item: { status: string }) => item.inviteFlag === "accept",
    );
    if (acceptedData?.length > 0 && acceptedData?.length < members?.length) {
      return `Accept invitation to be added in a group, verify ${pural} to add to your group`;
    } else if (
      acceptedData?.length > 0 &&
      acceptedData?.length == members?.length
    ) {
      return `Accept invitation to be added in a group`;
    } else {
      return `Verify ${pural} to add to your group`;
    }
  } else {
    return `Add ${myGroup} joining along with you`;
  }
};

/**
 * @description this function is used to decrypt the data
 * @param encryptedData
 * @returns decrypted string
 */
export const decryptData = (encryptedData: string): string => {
  try {
    // Return empty string if no data provided
    if (!encryptedData) return "";

    // Split the encrypted data into IV and actual encrypted content
    // Format is "IV:encryptedContent"
    const [ivHex, encryptedHex] = encryptedData.split(":");

    // Convert the hexadecimal IV string to CryptoJS WordArray format
    const iv = CryptoJS.enc.Hex.parse(ivHex);

    // Convert the hexadecimal encrypted content to CryptoJS WordArray
    const ciphertext = CryptoJS.enc.Hex.parse(encryptedHex);

    // Convert the encryption key from UTF8 string to WordArray
    const key = CryptoJS.enc.Utf8.parse(
      `${process.env.REACT_APP_DECRYPTION_KEY}`,
    );

    // Perform decryption using AES
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertext }, // Pass encrypted data as object
      key, // Encryption key
      {
        iv: iv, // Initialization Vector
        mode: CryptoJS.mode.CBC, // Cipher Block Chaining mode
        padding: CryptoJS.pad.Pkcs7, // Standard padding scheme
      },
    );

    // Convert the decrypted WordArray to UTF8 string
    const plaintext = decrypted.toString(CryptoJS.enc.Utf8);

    return plaintext;
  } catch (error) {
    console.error("Decryption error:", error);
    return "";
  }
};

/**
 * @description this function is used to redirect the user to zoom link in other tab
 * @param meetingId
 * @param seekerId
 */
export const handleZoomRedirection = (
  setSmallLoading: (loading: boolean) => void,
) => {
  const selectedMeetingData = getItemInLocalStorage("selectedMeeting");
  const seekerDetails = getItemInLocalStorage("seekerDetails");
  getCall(
    `${endPoints.webinars}/${selectedMeetingData?.meetingId}/user/${seekerDetails?.id}/join-details`,
  )
    .then((response) => {
      if (response?.data?.statusCode === 200) {
        const meetingUrl =
          response?.data?.data?.joinUrl || response?.data?.data?.startUrl;
        if (meetingUrl) {
          window.open(meetingUrl, "_blank");
          setSmallLoading(false); // Stop loading after opening the meeting URL
        } else {
          console.warn("No Zoom link available.");
          setSmallLoading(false); // Stop loading if no Zoom link is available
        }
      } else {
        console.log(
          "The session has been ended, you are being redirected to the home page…",
        );
        setSmallLoading(false); // Stop loading if the session has ended
      }
    })
    .catch((err: unknown) => {
      console.log(err, "error");
      setSmallLoading(false); // Stop loading in case of an error
    });
};

/**
 * @description this function is used to mark the attendance of the seeker when clicked on the join infinipath in the join with others
 */
export const joinmeetingAttendance = async () => {
  const verificationStatus = getItemInLocalStorage("verification_status");
  const seekerDetails = getItemInLocalStorage("seekerDetails");
  const browser = detectBrowser();
  const selectedMeetingData = getItemInLocalStorage("selectedMeeting");
  const payload = {
    userId: seekerDetails?.id, // pass userId not mobile number
    modeOfJoining: "SELF", //SELF,JOINING_ON_OTHER_DEVICE
    faceVerificationStatus: verificationStatus?.includes("success")
      ? true
      : false, // true or false
    attendees: getItemInLocalStorage("attendees")?.length
      ? getItemInLocalStorage("attendees")
      : [],
    // meeting_id: tableMeetingId || "",
    deviceType: "Browser",
    deviceInfo: browser?.toUpperCase(),
  };
  await postCall(
    `${endPoints.webinars}/${selectedMeetingData?.meetingId}/attendance`,
    payload,
  )
    .then((response) => {
      console.log(response, "Attendance marked2222");
      removeItemInLocalStorage("attendees");
      removeItemInLocalStorage("verification_status");
    })
    .catch((err) => {
      console.log(err);
    });
};

/**
 * @description this function is used to mask the phone number
 * @param number
 * @returns masked phone number
 * */

export const maskPhoneNumber = (number: string) => {
  if (!number || number.length < 5) {
    return number; // Return the original number if it's too short to mask
  }
  const maskedSection = "X".repeat(number.length - 5); // Mask the middle section
  return `${number.slice(0, 2)}${maskedSection}${number.slice(-3)}`; // Combine first 2 and last 3 digits
};

// Add this helper function at the top of your file after imports
export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

export const formatDateRangeToIST = (
  startDateStr: string,
  endDateStr?: string,
  includeSlot: boolean = true,
): string => {
  const toIST = (utcDateStr: string): Date => {
    const date = new Date(utcDateStr);
    const istOffsetMs = 5.5 * 60 * 60 * 1000;
    return new Date(date.getTime() + istOffsetMs);
  };

  const formatDayDate = (d: Date): string => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const day = days[d.getUTCDay()];
    const date = d.getUTCDate();
    const month = months[d.getUTCMonth()];
    return `${day}, ${month} ${date}`;
  };

  const formatTime = (d: Date): string => {
    let hours = d.getUTCHours();
    const minutes = d.getUTCMinutes();
    const ampm = hours >= 12 ? "p.m." : "a.m.";
    hours = hours % 12;
    hours = hours === 0 ? 12 : hours;
    return `${hours}${minutes !== 0 ? `:${minutes.toString().padStart(2, "0")}` : ""} ${ampm}`;
  };

  const startIST = toIST(startDateStr);

  if (endDateStr) {
    const endIST = toIST(endDateStr);

    const sameDay =
      startIST.getUTCFullYear() === endIST.getUTCFullYear() &&
      startIST.getUTCMonth() === endIST.getUTCMonth() &&
      startIST.getUTCDate() === endIST.getUTCDate();

    if (sameDay) {
      if (includeSlot) {
        const startEndIST = new Date(startIST.getTime() + 75 * 60 * 1000);
        const endEndIST = new Date(endIST.getTime() + 75 * 60 * 1000);

        const sameSlot =
          startIST.getUTCHours() === endIST.getUTCHours() &&
          startIST.getUTCMinutes() === endIST.getUTCMinutes();

        if (sameSlot) {
          return `${formatDayDate(startIST)} (${formatTime(startIST)} to ${formatTime(startEndIST)} IST)`;
        } else {
          return (
            `${formatDayDate(startIST)} (${formatTime(startIST)} to ${formatTime(startEndIST)} IST and ` +
            `${formatTime(endIST)} to ${formatTime(endEndIST)} IST)`
          );
        }
      } else {
        const sameTime =
          startIST.getUTCHours() === endIST.getUTCHours() &&
          startIST.getUTCMinutes() === endIST.getUTCMinutes();

        if (sameTime) {
          return `${formatDayDate(startIST)} (${formatTime(startIST)} IST)`;
        } else {
          return `${formatDayDate(startIST)} (${formatTime(startIST)} IST and ${formatTime(endIST)} IST)`;
        }
      }
    } else {
      if (includeSlot) {
        const startEndIST = new Date(startIST.getTime() + 75 * 60 * 1000);
        const endEndIST = new Date(endIST.getTime() + 75 * 60 * 1000);
        return (
          `${formatDayDate(startIST)} (${formatTime(startIST)} to ${formatTime(startEndIST)} IST) to ` +
          `${formatDayDate(endIST)} (${formatTime(endIST)} to ${formatTime(endEndIST)} IST)`
        );
      } else {
        return `${formatDayDate(startIST)} (${formatTime(startIST)} IST) to ${formatDayDate(endIST)} (${formatTime(endIST)} IST)`;
      }
    }
  } else {
    if (includeSlot) {
      const endIST = new Date(startIST.getTime() + 75 * 60 * 1000);
      return `${formatDayDate(startIST)} (${formatTime(startIST)} to ${formatTime(endIST)} IST)`;
    } else {
      return `${formatDayDate(startIST)} (${formatTime(startIST)} IST)`;
    }
  }
};

export const formateDate = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

export function capitalizeName(name: string = ""): string {
  return name
    ? name
        .split(" ")
        .map((word) =>
          word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : "",
        )
        .join(" ")
    : "";
}

export async function downloadFileFromUrl(url: string, filename?: string) {
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) throw new Error("Network response was not ok");
    const blob = await response.blob();
    const downloadName = filename || url.split("/").pop() || "downloaded-file";
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute("download", downloadName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error("Download failed:", error);
  }
}

export function calCulatePercentage(value: number, total: number) {
  return (value / total) * 100;
}
export function contentDateFormat(
  dateString: string,
  nextDay: boolean = false,
): string {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  // If nextDay is true, add one day to the date
  if (nextDay) {
    date.setDate(date.getDate() + 1);
  }

  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayOfWeek = daysOfWeek[date.getDay()];
  const day = date.getDate().toString().padStart(2, "0");
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  return `${dayOfWeek}, ${day} ${month} ${year}`;
}

export function extractTime(dateString: string): string {
  console.log(dateString, "hours period");

  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const period = hours >= 12 ? "p.m." : "a.m.";

  // Convert to 12-hour format
  hours = hours % 12 || 12;
  console.log(hours, minutes, period, "hours period");
  return `${hours}:${minutes} ${period}`;
}

export const formatPriceWithCommas = (price: string | number): string => {
  // Convert string to number if needed
  const numericPrice = typeof price === "string" ? parseFloat(price) : price;

  // Check if it's a valid number
  if (isNaN(numericPrice)) return "0";

  // Remove decimal if it's .00
  const isWholeNumber = numericPrice % 1 === 0;
  const numberToFormat = isWholeNumber
    ? Math.floor(numericPrice)
    : numericPrice;

  // Convert to string for formatting
  let priceStr = numberToFormat.toString();

  // Handle decimal part
  const [integerPart, decimalPart] = priceStr.split(".");

  // Apply Indian numbering system (first group of 3 from right, then groups of 2)
  const formatWithIndianCommas = (num: string): string => {
    if (num.length <= 3) return num;

    // Reverse the string to work from right to left
    const reversed = num.split("").reverse().join("");
    const groups: string[] = [];

    // First group of 3 digits
    groups.push(reversed.substring(0, 3));

    // Remaining groups of 2 digits
    let remaining = reversed.substring(3);
    while (remaining.length > 0) {
      groups.push(remaining.substring(0, 2));
      remaining = remaining.substring(2);
    }

    // Join groups with commas and reverse back
    return groups.join(",").split("").reverse().join("");
  };

  const formattedInteger = formatWithIndianCommas(integerPart);

  // Return with or without decimal part
  if (decimalPart && parseFloat(`0.${decimalPart}`) > 0) {
    return `${formattedInteger}.${decimalPart}`;
  }

  return formattedInteger;
};

/**
 * Formats a given date into a string representing the academic or fiscal year span.
 * For example, if the input date is in 2023, the output will be "2023-24".
 *
 * @param date - The date to format, as a `Date` object or an ISO date string.
 * @returns A string in the format "YYYY-YY", where "YYYY" is the start year and "YY" is the last two digits of the next year.
 */
export const formateYearDate = (date: Date | string): string => {
  const yearsDate = new Date(date);
  const startYear = yearsDate ? yearsDate.getFullYear().toString() : "";
  const nextYear = yearsDate
    ? (yearsDate.getFullYear() + 1).toString().slice(-2)
    : "";
  return `${startYear}-${nextYear}`;
};

export const hasApprovedStatusOptimized = (tracks: any[]): boolean => {
  if (!tracks || !Array.isArray(tracks)) {
    return false;
  }

  for (const track of tracks) {
    if (track.approvalStatus === "approved") {
      return true; // Early break
    }
  }

  return false;
};

export function formatTimeForHDBCards(dateTimeString: string): string {
  // Create date object and get local time components
  const date = new Date(dateTimeString);
  const hours = date.getHours();
  const minutes = date.getMinutes();

  // Convert to 12-hour format
  const hours12 = hours % 12 || 12;
  const period = hours >= 12 ? "p.m." : "a.m.";

  // Format the time string
  return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
}

// Simple Indian currency formatter
export const formatIndianCurrency = (amount: number | string): string => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return `${numAmount.toLocaleString('en-IN')}`;
};
